import { Suspense, lazy, useEffect, useState } from "react";
import { lazyRoute } from "@/utils/lazyRoute";
import { EnhancedToaster } from "@/components/ui/enhanced-toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth, AuthProvider } from "@/hooks/useAuth";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SkipToContent } from "@/components/SkipToContent";
import { ConnectionStatus } from "@/components/ConnectionStatus";
import { SEOHead } from "@/components/SEOHead";
import { WebsiteStructuredData } from "@/components/StructuredData";
// FloatingNeuralAssistant is heavy (voice chat, GSAP, Supabase, page context).
// Lazy-load it and mount only after the browser is idle so it never contributes
// to the first-input long task on page load.
const FloatingNeuralAssistant = lazy(() =>
  import("@/components/FloatingNeuralAssistant").then((m) => ({
    default: m.FloatingNeuralAssistant,
  }))
);
// Eager-load the homepage routes so the LCP H1 paints in the first bundle round-trip
// (avoids a second chunk fetch that was adding ~2.7s to LCP element render delay).
import Discovery from "./pages/Discovery";
import Index from "./pages/Index";
// Lazy load remaining routes for code splitting
const AuthorMatrix = lazyRoute(() => import("./pages/AuthorMatrix"), "AuthorMatrix");
const ReadingInsights = lazyRoute(() => import("./pages/ReadingInsights"), "ReadingInsights");
const Search = lazyRoute(() => import("./pages/Search"), "Search");
const BookBrowser = lazyRoute(() => import("./pages/BookBrowser"), "BookBrowser");
const PublisherResonance = lazyRoute(() => import("./pages/PublisherResonance"), "PublisherResonance");
const Auth = lazyRoute(() => import("./pages/Auth"), "Auth");
const TestBrain = lazyRoute(() => import("./pages/TestBrain"), "TestBrain");
const Community = lazyRoute(() => import("./pages/Community"), "Community");
const BookToScreen = lazyRoute(() => import("./pages/BookToScreen"), "BookToScreen");
const FreeToRead = lazyRoute(() => import("./pages/FreeToRead"), "FreeToRead");
const Protagonists = lazyRoute(() => import("./pages/Protagonists"), "Protagonists");
const PhilipKDick = lazyRoute(() => import("./pages/PhilipKDick"), "PhilipKDick");
const AdminEnrichment = lazyRoute(() => import("./pages/AdminEnrichment"), "AdminEnrichment");
const Unsubscribe = lazyRoute(() => import("./pages/Unsubscribe"), "Unsubscribe");
const NewsletterConfirm = lazyRoute(() => import("./pages/NewsletterConfirm"), "NewsletterConfirm");
const ProtagonistApp = lazyRoute(() => import("./pages/ProtagonistApp"), "ProtagonistApp");
const Clubs = lazyRoute(() => import("./pages/Clubs"), "Clubs");
const ClubDetail = lazyRoute(() => import("./pages/ClubDetail"), "ClubDetail");
const ClubBookHub = lazyRoute(() => import("./pages/ClubBookHub"), "ClubBookHub");
const PrivacyPolicy = lazyRoute(() => import("./pages/PrivacyPolicy"), "PrivacyPolicy");
const About = lazyRoute(() => import("./pages/About"), "About");

const NotFound = lazyRoute(() => import("./pages/NotFound"), "NotFound");
const ShareBook = lazyRoute(() => import("./pages/ShareBook"), "ShareBook");

const queryClient = new QueryClient();

// Scroll restoration component
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
    // Do NOT clear lazyRoute's chunk-reload flags here. This effect runs on
    // MOUNT as well as on navigation, so the reload lazyRoute triggers would
    // remount the app and wipe the flag that reload had just set — failing the
    // import again, reloading again, for ever. Each flag is now released by a
    // successful load of its own chunk, in lazyRoute.
  }, [pathname]);

  return null;
};

/**
 * Titles a private/internal route and keeps it out of the index.
 *
 * Mounted here rather than inside each page because these components have
 * several early returns (loading, redirect, error) — an internal <SEOHead>
 * would not mount on every path, and since SEOHead only ever assigns
 * document.title and never resets it, any missed path left the *previous*
 * page's title in the tab.
 */
const PrivatePage = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <>
    <SEOHead title={title} noindex />
    {children}
  </>
);

const AppRoutes = () => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Detect a Supabase password-recovery return so the /auth route is NOT
  // redirected away (the recovery link establishes a session, making `user`
  // truthy — without this the set-new-password form could never render).
  const isRecovery =
    new URLSearchParams(location.search).get("type") === "recovery" ||
    location.hash.includes("type=recovery");

  // Only block first render on auth resolution for routes whose output depends
  // on it. Public read-only routes (home, /book-browser, …) render immediately
  // instead of waiting behind a session check / token refresh they never use.
  const authSensitivePaths = ["/insights", "/admin", "/auth"];
  const isAuthSensitiveRoute = authSensitivePaths.some((p) =>
    location.pathname.startsWith(p)
  );

  if (loading && isAuthSensitiveRoute) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full border-2 border-dashed border-slate-600 flex items-center justify-center">
            <div className="w-6 h-6 rounded-full border-2 border-blue-400 animate-pulse" />
          </div>
          <p className="text-slate-400">Establishing connection...</p>
        </div>
      </div>
    );
  }

  // Loading fallback for lazy-loaded components
  const SuspenseFallback = (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full border-2 border-dashed border-slate-600 flex items-center justify-center">
          <div className="w-6 h-6 rounded-full border-2 border-blue-400 animate-pulse" />
        </div>
        <p className="text-slate-400">Loading...</p>
      </div>
    </div>
  );

  // All main read-only routes are accessible to everyone.
  // Auth-gated routes (admin, insights) redirect unauthenticated users to /auth.
  return (
    <>
      <Suspense fallback={SuspenseFallback}>
        {/* Per-route boundary: without this the only ErrorBoundary was at the
            app root, so one route throwing during render replaced the entire
            application. Keying on pathname remounts (and so resets) the
            boundary on navigation, meaning a broken page recovers by moving
            away from it instead of trapping the user. */}
        <ErrorBoundary key={location.pathname}>
        <Routes>
          {/* Public routes — accessible to all visitors */}
          <Route path="/" element={<Discovery />} />
          <Route path="/library" element={<Index />} />
          <Route path="/thread-map" element={<Navigate to="/book-browser" replace />} />
          <Route path="/author-matrix" element={<AuthorMatrix />} />
          <Route path="/search" element={<Search />} />
          <Route path="/book-browser" element={<BookBrowser />} />
          <Route path="/publisher-resonance" element={<PublisherResonance />} />
          <Route path="/test-brain" element={<PrivatePage title="Neural Map"><TestBrain /></PrivatePage>} />
          <Route path="/community" element={<Community />} />
          <Route path="/clubs" element={<Clubs />} />
          <Route path="/clubs/:slug" element={<ClubDetail />} />
          <Route path="/clubs/:slug/books/:pickId" element={<ClubBookHub />} />
          <Route path="/protagonists" element={<Protagonists />} />
          {/* Single-author room. /authors/:slug can generalise this later. */}
          <Route path="/philip-k-dick" element={<PhilipKDick />} />
          <Route path="/book-to-screen" element={<BookToScreen />} />
          {/* Public: these texts are out of copyright and need no account. */}
          <Route path="/free-to-read" element={<FreeToRead />} />
          <Route path="/unsubscribe/:token" element={<PrivatePage title="Unsubscribe"><Unsubscribe /></PrivatePage>} />
          <Route path="/newsletter/confirm/:token" element={<PrivatePage title="Confirm subscription"><NewsletterConfirm /></PrivatePage>} />
          <Route path="/protagonist-app" element={<ProtagonistApp />} />
          <Route path="/share/book/:id" element={<ShareBook />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/about" element={<About />} />

          {/* Auth-gated routes */}
          <Route path="/insights" element={user ? <PrivatePage title="Reading Insights"><ReadingInsights /></PrivatePage> : <Navigate to="/auth" replace />} />
          <Route path="/admin/enrichment" element={user ? <PrivatePage title="Admin"><AdminEnrichment /></PrivatePage> : <Navigate to="/auth" replace />} />
          <Route path="/admin/populate" element={<Navigate to="/admin/enrichment" replace />} />

          {/* Auth page — redirect home if already signed in (but stay during recovery) */}
          <Route path="/auth" element={user && !isRecovery ? <Navigate to="/" replace /> : <PrivatePage title="Sign in"><Auth /></PrivatePage>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </ErrorBoundary>
      </Suspense>
      {/* Global floating AI assistant — deferred until idle */}
      <ErrorBoundary fallback={null}>
        <DeferredAssistant />
      </ErrorBoundary>
    </>
  );
};

// Mounts the FloatingNeuralAssistant only after the browser is idle, so its
// JS chunk and initialisation never block first input on page load.
const DeferredAssistant = () => {
  const [shouldMount, setShouldMount] = useState(false);

  useEffect(() => {
    type WindowWithIdleCallback = Window & typeof globalThis & {
      requestIdleCallback?: (callback: () => void) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    const browserWindow = window as WindowWithIdleCallback;
    const idle =
      browserWindow.requestIdleCallback ||
      ((cb: () => void) => window.setTimeout(cb, 1500));
    const handle = idle(() => setShouldMount(true));
    return () => {
      const cancel =
        browserWindow.cancelIdleCallback ||
        window.clearTimeout;
      cancel(handle);
    };
  }, []);

  if (!shouldMount) return null;
  return (
    <Suspense fallback={null}>
      <FloatingNeuralAssistant />
    </Suspense>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <SkipToContent />
        <WebsiteStructuredData
          name="Leafnode"
          description="Track, discover, and explore science fiction literature with AI-powered insights"
          url={typeof window !== 'undefined' ? window.location.origin : 'https://www.leafnode.co.uk'}
        />
        <ConnectionStatus />
        <EnhancedToaster />
        <Sonner />
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
